import { useEffect } from 'react';
import { buildPageStructuredData, type StructuredData } from '@/lib/seo';

interface PageSEOProps {
  title: string;
  description: string;
  canonicalPath: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  jsonLd?: StructuredData | StructuredData[];
  keywords?: string;
}

const SITE_DOMAIN = 'https://nexapods.com.au';

function setOrCreateMeta(name: string, content: string, isProperty = false): void {
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(name: string, isProperty = false): void {
  const attr = isProperty ? 'property' : 'name';
  const el = document.querySelector(`meta[${attr}="${name}"]`);
  if (el) el.remove();
}

export default function PageSEO({
  title,
  description,
  canonicalPath,
  ogTitle,
  ogDescription,
  ogImage = 'https://nexapods.com.au/images/dabe9d51796d.webp',
  twitterTitle,
  twitterDescription,
  twitterImage,
  jsonLd,
  keywords,
}: PageSEOProps) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    setOrCreateMeta('description', description);
    if (keywords) setOrCreateMeta('keywords', keywords);

    const canonicalUrl = `${SITE_DOMAIN}${canonicalPath}`;
    let canonicalEl = document.querySelector('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', canonicalUrl);

    setOrCreateMeta('og:title', ogTitle || title, true);
    setOrCreateMeta('og:description', ogDescription || description, true);
    setOrCreateMeta('og:type', 'website', true);
    setOrCreateMeta('og:url', canonicalUrl, true);
    if (ogImage) setOrCreateMeta('og:image', ogImage, true);

    setOrCreateMeta('twitter:card', 'summary_large_image');
    setOrCreateMeta('twitter:title', twitterTitle || ogTitle || title);
    setOrCreateMeta('twitter:description', twitterDescription || ogDescription || description);
    if (twitterImage || ogImage) setOrCreateMeta('twitter:image', twitterImage || ogImage || '');

    let jsonLdEl = document.querySelector('script[type="application/ld+json"][data-page-seo]');
    if (!jsonLdEl) {
      jsonLdEl = document.createElement('script');
      jsonLdEl.setAttribute('type', 'application/ld+json');
      jsonLdEl.setAttribute('data-page-seo', 'true');
      document.head.appendChild(jsonLdEl);
    }
    jsonLdEl.textContent = JSON.stringify(buildPageStructuredData({
      title,
      canonicalPath,
      pageJsonLd: jsonLd,
    }));

    return () => {
      document.title = prevTitle;
      removeMeta('description');
      if (keywords) removeMeta('keywords');
      removeMeta('og:title', true);
      removeMeta('og:description', true);
      removeMeta('og:url', true);
      removeMeta('og:image', true);
      removeMeta('twitter:title');
      removeMeta('twitter:description');
      removeMeta('twitter:image');
      const ld = document.querySelector('script[type="application/ld+json"][data-page-seo]');
      if (ld) ld.remove();
      const can = document.querySelector('link[rel="canonical"]');
      if (can) can.remove();
    };
  }, [title, description, canonicalPath, ogTitle, ogDescription, ogImage, twitterTitle, twitterDescription, twitterImage, jsonLd, keywords]);

  return null;
}
